// @ts-nocheck
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ChevronRight, Calendar, ArrowRight, Palette, Clock, Users } from 'lucide-react';
import { formatToEST } from '@/utils/dateUtils';

interface Auction {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  location: string;
  image_url: string;
  lots: Lot[];
}

interface Lot {
  id: string;
  lot_number: number;
  starting_bid: number;
  current_bid: number;
  status: string;
  artworks: {
    title: string;
    image_url: string;
    additional_images: any[];
    medium: string;
    dimensions: string;
    year: number;
    artist: {
      name: string;
    };
  };
}

export default function PastAuctions() {
  const { data: pastAuctions, isLoading } = useQuery({
    queryKey: ['pastAuctions'],
    queryFn: async () => {
      // Get all past (completed) auctions, newest first
      const { data: allAuctions, error: auctionsError } = await supabase
        .from('auctions')
        .select('id, title, description, start_date, end_date, status, location, image_url')
        .eq('status', 'completed')
        .order('end_date', { ascending: false });

      if (auctionsError) throw auctionsError;
      
      // Then get lots for each auction with statistics
      const auctionsWithLots = await Promise.all(
        (allAuctions || []).map(async (auction: any) => {
          const { data: lots, error: lotsError } = await supabase
            .from('lots')
            .select(`
              id,
              lot_number,
              starting_bid,
              current_bid,
              status,
              artworks (
                title,
                image_url,
                additional_images,
                medium,
                dimensions,
                year,
                artist:artist_id (
                  name
                )
              )
            `)
            .eq('auction_id', auction.id)
            .order('lot_number');

          if (lotsError) {
            console.error('Error fetching lots for auction:', auction.id, lotsError);
            return { ...auction, lots: [], statistics: { lotsSold: 0, lotsPassed: 0, totalSales: 0 } };
          }

          // Calculate statistics
          const validLots = lots || [];
          const soldLots = validLots.filter((lot: any) => lot.status === 'sold');
          const passedLots = validLots.filter((lot: any) => lot.status === 'passed');
          const totalSales = soldLots.reduce((sum: number, lot: any) => {
            return sum + (lot.current_bid || 0);
          }, 0);

          return { 
            ...auction, 
            lots: validLots,
            statistics: {
              lotsSold: soldLots.length,
              lotsPassed: passedLots.length,
              totalSales: totalSales
            }
          };
        })
      );

      return auctionsWithLots as Auction[];
    },
  });

  if (isLoading) {
    return (
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex justify-between items-center mb-10">
            <div>
              <div className="inline-flex items-center justify-center px-6 py-2 mb-4 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
                <span className="text-teal-400 font-light tracking-wide">Our History</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extralight text-white tracking-wide">Past Auctions</h2>
            </div>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10">
                <div className="h-64 bg-white/10 rounded-t-2xl"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-white/10 rounded w-3/4"></div>
                  <div className="h-4 bg-white/10 rounded w-1/2"></div>
                  <div className="h-4 bg-white/10 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Sophisticated Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
      </div>

      {/* Professional pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Professional Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
            <Palette className="h-5 w-5 mr-3 text-emerald-400" />
            <span className="text-white font-light tracking-wide">Our Past Auction</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
            <span className="block">Discover Florida</span>
            <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">Highwaymen Art</span>
          </h2>
          
          <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light mb-8">
            Explore our extensive past auction collection, <span className="text-emerald-300 font-normal">Florida Highwaymen artwork</span> from legendary artists with our newest completed auction, the <span className="text-emerald-300 font-normal">"August 23rd 2025 Auction"</span>
          </p>

          <Link to="/auctions?tab=past" className="inline-flex items-center text-emerald-300 hover:text-emerald-200 font-light tracking-wide transition-colors">
            View all past auctions <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>

        {pastAuctions && pastAuctions.length > 0 ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {pastAuctions.map((auction) => {
              // Get first 3 lots with images
              const firstThreeLots = auction.lots
                .filter(lot => lot.artworks?.image_url)
                .slice(0, 3);

              return (
                <Link 
                  key={auction.id}
                  to={`/auctions/${auction.id}`} 
                  className="block group"
                >
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 shadow-lg group-hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl overflow-hidden hover:-translate-y-1">
                    {/* Custom 3-image layout */}
                    <div className="relative h-64 w-full overflow-hidden">
                      {firstThreeLots.length >= 3 ? (
                        <div className="flex h-full">
                          {/* First image - left half */}
                          <div className="w-1/2 h-full relative">
                            <img 
                              src={firstThreeLots[0].artworks.image_url} 
                              alt={firstThreeLots[0].artworks.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            <div className="absolute bottom-2 left-2 text-white text-xs font-medium">
                              Lot {firstThreeLots[0].lot_number}
                            </div>
                          </div>
                          
                          {/* Right half - split into top and bottom */}
                          <div className="w-1/2 h-full flex flex-col">
                            {/* Second image - top right */}
                            <div className="h-1/2 relative">
                              <img 
                                src={firstThreeLots[1].artworks.image_url} 
                                alt={firstThreeLots[1].artworks.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                              <div className="absolute bottom-1 left-1 text-white text-xs font-medium">
                                Lot {firstThreeLots[1].lot_number}
                              </div>
                            </div>
                            
                            {/* Third image - bottom right */}
                            <div className="h-1/2 relative">
                              <img 
                                src={firstThreeLots[2].artworks.image_url} 
                                alt={firstThreeLots[2].artworks.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                              <div className="absolute bottom-1 left-1 text-white text-xs font-medium">
                                Lot {firstThreeLots[2].lot_number}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Fallback for auctions with less than 3 lots
                        <div className="w-full h-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 backdrop-blur-sm flex items-center justify-center">
                          <div className="text-center">
                            <Palette className="h-12 w-12 text-emerald-300 mx-auto mb-2" />
                            <p className="text-white text-sm font-light">
                              {auction.lots.length} Lots
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Auction status overlay */}
                      <div className="absolute top-3 right-3">
                        <div className="bg-emerald-500/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                          Completed
                        </div>
                      </div>
                    </div>

                    {/* Professional Auction details */}
                    <div className="p-6 flex-grow flex flex-col">
                      <h3 className="text-xl font-light text-white mb-2 line-clamp-1 group-hover:text-emerald-300 transition-colors tracking-wide">
                        {auction.title}
                      </h3>
                      
                      <div className="flex items-center text-sm text-white/60 mb-3">
                        <Calendar className="h-4 w-4 mr-1.5 text-emerald-400" />
                        <span className="font-light">{formatToEST(auction.start_date)}</span>
                      </div>

                      <div className="flex items-center text-sm text-white/60 mb-3">
                        <Users className="h-4 w-4 mr-1.5 text-blue-400" />
                        <span className="font-light">{auction.lots.length} lots</span>
                      </div>

                      <p className="text-sm text-white/50 flex-grow line-clamp-2 mb-4 font-light leading-relaxed">
                        {auction.description || 'A collection of Florida Highwaymen artwork featuring authentic pieces from legendary artists.'}
                      </p>

                      <div className="mt-auto pt-3 border-t border-white/10">
                        <span className="text-emerald-300 text-sm font-light flex items-center hover:text-emerald-200 transition-colors">
                          View Results <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-xl p-12 max-w-2xl mx-auto">
              <Clock className="h-16 w-16 text-white/30 mx-auto mb-6" />
              <h3 className="text-2xl font-light text-white mb-4 tracking-wide">
                No Past Auctions Yet
              </h3>
              <p className="text-white/70 font-light leading-relaxed">
                Our auction history will appear here once we complete our first events.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
} 